import { PolicyTypeCd } from '@aaa-ncnu-ie/web-mypolicy-api-billing';
import {
  Button,
  Card,
  Stack,
  Typography,
  Divider,
  FormControlLabel,
  Checkbox,
  Box,
  Link as MuiLink,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import { MouseEvent, useMemo, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { Link, Navigate, useNavigate } from 'react-router-dom';

import { EnrollInAutoPayArgs, enrollInAutoPay } from '../../api/autoPay/enroll';
import { useBillsQuery } from '../../api/getBills';
import { useWelcomeQuery } from '../../api/getWelcomeDetails';
import {
  SubmitPaymentDetails,
  submitPayment,
  LineItem,
  SubmitMultiPolicyPayReq,
  submitMultiPolicyPayments,
} from '../../api/submitPayment';
import ExitConfirmationModal from '../../components/ExitConfirmationModal';
import PaymentsCards from '../../components/PaymentsInfo/PaymentsCards';
import PaymentsTable from '../../components/PaymentsInfo/PaymentsTable';
import { LinkWithNoRouter } from '../../components/styledComponents/Link';
import StatusAlert from '../../components/styledComponents/StatusAlert';
import colors from '../../constants/colors';
import { PaymentAmountTypeLabels, StatusType } from '../../constants/enums';
import { AutoPayMessages } from '../../constants/paymentMessages';
import useAnalytics from '../../hooks/useAnalytics';
import usePathnameBlocker from '../../hooks/usePathnameBlocker';
import { useLastPayment } from '../../providers/LastPaymentProvider';
import { usePaymentsContext } from '../../store/payments';
import {
  addConfirmationEmail,
  addPaymentDetails,
  setAutoPayBanner,
} from '../../store/paymentsReducer';
import ReviewCardCommonEntry from '../Payment/components/ReviewCardCommonEntry';
import WithAutoPayDialog from './components/WithAutoPayDialog';
import WithOutAutoPayDialog from './components/WithOutAutoPayDialog';
import CircularProgress from '../../components/CircularProgress/CircularProgress';

const PaymentReviewPage = () => {
  const navigate = useNavigate();
  const { state, dispatch } = usePaymentsContext();
  const { data: billsData } = useBillsQuery();
  const { data: welcomeData } = useWelcomeQuery();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only('mobile'));
  const { setLastPayment } = useLastPayment();

  const userEmail = welcomeData?.userInfo?.email;
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isAutoPaySubmitting, setIsAutoPaySubmitting] =
    useState<boolean>(false);
  const [autoPayChecked, setAutoPayChecked] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [isErrorAlertOpen, setIsErrorAlertOpen] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const submitPaymentCall = useMutation({
    mutationFn: (args: SubmitPaymentDetails) => submitPayment(args),
  });
  const submitMultiPolicyPaymentsCall = useMutation({
    mutationFn: (args: SubmitMultiPolicyPayReq) =>
      submitMultiPolicyPayments(args),
  });
  const { track } = useAnalytics();
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [paymentSubmitted, setPaymentSubmitted] = useState(false);

  const selectedPaymentMethod = state.selectedPaymentMethod!;

  const policyNumbers = Object.keys(state.selectedPayments).filter(
    (policyNumber) => Number(state.selectedPayments[policyNumber].amount) > 0
  );

  const enrollInAutoPayMutation = useMutation({
    mutationFn: (args: EnrollInAutoPayArgs) => enrollInAutoPay(args),
  });

  const pastDuePolicies = useMemo(
    () =>
      billsData?.payments
        ?.filter((payment) => {
          const pastDue = payment?.pastDue;
          const paymentSelected =
            policyNumbers.includes(payment?.policyNumber) ||
            state.selectedBill?.policyNumber === payment?.policyNumber;
          return pastDue && paymentSelected;
        })
        .map((payment) => payment.policyNumber),
    [billsData?.payments, state.selectedBill]
  );

  const isSinglePayment = !isEmpty(state.singleSelectedPayment);

  const showAutoPay = isSinglePayment && state.selectedBill.autoPay.eligible;

  const getTotal = () =>
    policyNumbers.reduce(
      (acc, policyNumber) =>
        acc + Number(state.selectedPayments[policyNumber].amount),
      0
    );

  const parseToCent = (n: string) =>
    Number(parseFloat(n).toFixed(2).replace(/\./, ''));

  //Mobile Mypolicy has this logic to handle decimal precision for numbers during multi policy submission
  const getTotalPaymentAmount = () => {
    const amountInCent = policyNumbers.reduce(
      (currentAmount, policyNumber) =>
        currentAmount +
        parseToCent(state.selectedPayments[policyNumber].amount ?? '0'),
      0
    );
    return amountInCent > 0 ? amountInCent / 100 : amountInCent;
  };

  const paymentType = isSinglePayment ? 'One Policy' : 'Multiple Policies';
  let analyticsProps = {};
  if (isSinglePayment) {
    analyticsProps = {
      policy_number: state.singleSelectedPayment.policyNumber,
      state: state.singleSelectedPayment.riskState,
      policy_type: state.singleSelectedPayment.policyType,
    };
  } else {
    analyticsProps = {
      total_payment: getTotal().toFixed(2),
    };
  }

  const paymentEvent = (allowBulk: boolean, success: boolean) => {
    if (!allowBulk && !isSinglePayment) return;
    track('Payment Submitted', {
      event_type: 'User Information Entered',
      location: 'Feature Card',
      account_type: state.selectedPaymentMethod?.analyticsAccountType,
      payment_type: paymentType,
      payment_amount: state.singleSelectedPayment.amount,
      payment_selection:
        PaymentAmountTypeLabels[state.singleSelectedPayment.paymentType],
      status: success ? 'Succeeded' : 'Failed',
      autopay: autoPayChecked,
      ...analyticsProps,
    });
    if (isSinglePayment && success) {
      track('Autopay Selection Confirmed', {
        status: 'Succeeded',
        selection: autoPayChecked ? 'Autopay Selected' : 'Payment Only',
        autopay: autoPayChecked,
        event_type: 'Option Selected',
        location: 'Feature Card',
        ...analyticsProps,
      });
    }
  };

  const handlePayClick = async (shouldEnrollInAutoPay: boolean) => {
    if (shouldEnrollInAutoPay) {
      setIsAutoPaySubmitting(true);
      setIsSubmitting(false);
    } else {
      setIsSubmitting(true);
      setIsAutoPaySubmitting(false);
    }
    const paymentObj = {
      email: userEmail,
      paymentAccountToken: selectedPaymentMethod?.paymentToken ?? '',
      zipCode: selectedPaymentMethod?.zipCode ?? '',
      expirationDate: selectedPaymentMethod?.expirationDate ?? '',
      paymentMethod: selectedPaymentMethod?.paymentMethod,
    };

    if (isSinglePayment) {
      const {
        policyNumber,
        policyTypeCd,
        dataSource,
        riskState,
        underwritingCompany,
      } = state.selectedBill;

      const singlePaymentObj = {
        ...paymentObj,
        amount: state.singleSelectedPayment.amount!,
        policyNumber,
        dataSource,
        policyTypeCd,
        riskState,
        underwritingCompany,
      };

      const res = await sendPayment(singlePaymentObj, shouldEnrollInAutoPay);

      if (res) {
        // refetch the bills to hopefully display an updated payment status
        queryClient.invalidateQueries({
          queryKey: ['bills'],
        });
        navigate('/payment/status');
      } else {
        setIsErrorAlertOpen(true);
      }
    } else {
      const lineItems: LineItem[] = [];

      policyNumbers.forEach((policyNumber) => {
        const policyPayment = state.selectedPayments[policyNumber];
        if (policyPayment) {
          const lineItem = {
            amount: Number(policyPayment.amount!),
            policyNumber,
            dataSource: policyPayment?.dataSource ?? '',
            policyTypeCd: policyPayment?.policyTypeCd ?? '',
            riskState: policyPayment?.riskState ?? '',
            underwritingCompany: policyPayment?.underwritingCompany ?? '',
          };
          lineItems.push(lineItem);
        }
      });
      // using getTotal has incorrect value due to precision Issues,
      // e.g. values of $0.2 and $0.1 in getTotal returns 0.30000000000000004
      // use integer scaling similar to Mobile Mypolicy
      const totalAmount = getTotalPaymentAmount();
      const paymentSuccess = await sendMultiplePayment({
        totalAmount,
        paymentObj,
        lineItems,
      });

      if (paymentSuccess) {
        paymentEvent(true, true);
        // refetch the bills to hopefully display an updated payment status
        queryClient.invalidateQueries({
          queryKey: ['bills'],
        });
        navigate('/payment/status');
      } else {
        paymentEvent(true, false);
        setIsErrorAlertOpen(true);
      }
    }

    setPaymentSubmitted(true);
    setIsSubmitting(false);
  };

  const dispatchAutoPayError = () =>
    dispatch(
      setAutoPayBanner({
        text: (
          <Typography>
            Please try again to{' '}
            <MuiLink
              component={Link}
              to="/autopay"
              onClick={() =>
                track('Terms and Conditions Accessed', {
                  event_type: 'Link Accessed',
                  link: 'paperless preferences terms and conditions',
                  location: 'Modal',
                })
              }
              underline="none"
              sx={{
                color: 'primary',
                fontWeight: { mobile: 400, tablet: 700 },
                fontSize: { mobile: 16, tablet: 18 },
                fontFamily: 'Cabin',
              }}
            >
              set up or manage AutoPay
            </MuiLink>
            .
          </Typography>
        ),
        status: StatusType.ERROR,
        show: true,
        title: 'We are unable to process your AutoPay request right now.',
      })
    );

  const sendMultiplePayment = async (
    submitMultiPolicyPayReq: SubmitMultiPolicyPayReq
  ) => {
    try {
      const response = await submitMultiPolicyPaymentsCall.mutateAsync(
        submitMultiPolicyPayReq
      );

      if ('paymentDateTime' in response && response.paymentDateTime) {
        dispatch(addConfirmationEmail(userEmail ?? ''));
        submitMultiPolicyPayReq.lineItems.forEach((lineItem) => {
          dispatch(
            addPaymentDetails({
              [lineItem.policyNumber]: {
                paymentDateTime: response.paymentDateTime,
                receiptNumber: response.receiptNumber,
                externalReferenceNumber: response.externalReferenceNumber,
                authCode: response.authCode ?? '',
              },
            })
          );
        });
        return true;
      }
      //If there are is no paymentDatetime in response, then it is considered as error
      return false;
    } catch (err) {
      return false;
    }
  };

  const sendPayment = async (
    submitPaymentDetails: SubmitPaymentDetails,
    shouldEnrollInAutoPay?: boolean
  ) => {
    try {
      const response =
        await submitPaymentCall.mutateAsync(submitPaymentDetails);

      if ('paymentDateTime' in response && response.paymentDateTime) {
        if (shouldEnrollInAutoPay) {
          try {
            const policies = [
              {
                policyNumber: submitPaymentDetails.policyNumber,
                policyInfo: {
                  productType:
                    submitPaymentDetails.policyTypeCd as PolicyTypeCd,
                },
                paymentItem: {
                  paymentMethod: submitPaymentDetails.paymentMethod,
                  paymentAccountToken: submitPaymentDetails.paymentAccountToken,
                },
              },
            ];
            const { data } = await enrollInAutoPayMutation.mutateAsync({
              policies,
            });
            const foundFault = data.find((res) => 'fault' in res);

            if (foundFault) {
              dispatchAutoPayError();
            } else {
              const text = `${AutoPayMessages.SUCCESS_NEW} ${state.selectedBill.policyType} policy ${state.selectedBill.policyNumber}.`;
              dispatch(
                setAutoPayBanner({
                  text,
                  status: StatusType.SUCCESS,
                  show: true,
                })
              );
            }
          } catch (error: unknown) {
            console.error(error);
            dispatchAutoPayError();
          }
        }
        dispatch(addConfirmationEmail(userEmail ?? ''));
        dispatch(
          addPaymentDetails({
            [submitPaymentDetails.policyNumber]: {
              paymentDateTime: response.paymentDateTime,
              receiptNumber: response.receiptNumber,
              externalReferenceNumber: response.externalReferenceNumber,
              authCode: response.authCode ?? '',
            },
          })
        );
        setLastPayment({
          amount: Number(submitPaymentDetails.amount).toFixed(2),
          date: response.paymentDateTime,
          policyNumber: submitPaymentDetails.policyNumber,
        });
        paymentEvent(false, true);
        return true;
      } else {
        paymentEvent(false, false);
        if ('errorCode' in response) {
          paymentEvent(false, false);
        }
      }

      return false;
    } catch (err) {
      return false;
    }
  };

  const dialogButtonValue = autoPayChecked
    ? 'Confirm and submit'
    : 'Submit payment only';

  const renderPaymentButton = () => {
    if (showAutoPay && !isErrorAlertOpen) {
      if (autoPayChecked) {
        return (
          <WithAutoPayDialog
            open={open}
            onClose={() => setOpen(false)}
            onSubmit={() => handlePayClick(true)}
            isLoading={isSubmitting || isAutoPaySubmitting}
            buttonValue={dialogButtonValue}
          />
        );
      }
      return (
        <WithOutAutoPayDialog
          open={open}
          onClose={() => {
            setIsSubmitting(false);
            setOpen(false);
          }}
          onSubmit={() => handlePayClick(false)}
          isLoading={{
            payment: isSubmitting,
            autoPay: isAutoPaySubmitting,
          }}
          submitAndSetupAutoPay={() => handlePayClick(true)}
          buttonValue={dialogButtonValue}
        />
      );
    }

    return null;
  };

  const onPayClick = () => {
    // if a user somehow finds themselves on the review page without going through the initial steps we will redirect them to /bills
    if (!selectedPaymentMethod?.paymentToken) {
      navigate('/bills');
    }

    setIsErrorAlertOpen(false);

    if (showAutoPay) {
      setOpen(true);
    } else {
      handlePayClick(false);
    }
  };

  const navigationBlocker = usePathnameBlocker(
    ['/logout', '/payment/status', '/payment/review'],
    setShowExitDialog,
    paymentSubmitted
  );

  return (
    <>
      <ExitConfirmationModal
        open={showExitDialog}
        onClose={() => {
          setShowExitDialog(false);
        }}
        handleContinue={async () => {
          navigationBlocker();
        }}
        title="Are you sure you want to leave this page?"
        bodyText={['By selecting "Yes", your payment will not be completed.']}
        leaveText="Yes, leave"
        stayText="No, stay"
        showRemoveIcon
      />
      <Stack
        direction="column"
        data-testid="paymentReviewPage"
        spacing={{ mobile: 2, tablet: 3 }}
        sx={{
          width: 1,
          minHeight: '100%',
          backgroundColor: colors.csaaGray,
          px: { mobile: 2, desktop: 19.5 },
          pb: 3,
          pt: { mobile: 3, desktop: 9 },
        }}
      >
        {!selectedPaymentMethod && <Navigate to="/bills" replace />}
        <Typography
          variant="h1"
          sx={{
            fontWeight: { mobile: 400, tablet: 500 },
            fontSize: { mobile: 33, tablet: 44 },
            lineHeight: { mobile: '32px', tablet: '56px' },
          }}
        >
          Review payment
        </Typography>
        <StatusAlert
          text={
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: '18px',
                fontFamily: 'Cabin',
              }}
            >
              Please check the details and try again, try another payment
              method, or contact Service at{' '}
              <LinkWithNoRouter href="tel:+8889805650" sx={{ fontWeight: 700 }}>
                888.980.5650
              </LinkWithNoRouter>
              .
            </Typography>
          }
          open={isErrorAlertOpen}
          title="We are unable to process your request right now."
          status={StatusType.ERROR}
        />
        <StatusAlert
          text={
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: '18px',
                fontFamily: 'Cabin',
              }}
            >
              Payment may not reinstate a cancelled/expired policy. Call{' '}
              <LinkWithNoRouter href="tel:+8889805650" sx={{ fontWeight: 700 }}>
                888.980.5650
              </LinkWithNoRouter>{' '}
              for help.
            </Typography>
          }
          open={!!(pastDuePolicies && pastDuePolicies?.length > 0)}
          title="Payment is past due"
          status={StatusType.INFO}
          dataTestId="past-due-alert"
        />
        <Card
          component={Stack}
          spacing={3}
          sx={{
            borderRadius: 2.5,
            px: {
              mobile: 2,
              tablet: 3,
              desktop: 5,
            },
            py: {
              mobile: 2,
              tablet: 3,
              desktop: 5,
            },
            height: 'auto',
            boxShadow: '0px 4px 8px 0px rgba(25, 30, 43, 0.08)',
          }}
        >
          <Stack
            spacing={1}
            divider={<Divider />}
            sx={{
              border: '1px solid',
              borderColor: colors.csaaLightBorder,
              borderRadius: 2,
              p: 3,
            }}
          >
            <ReviewCardCommonEntry
              label="Payment method"
              testId="payment-review-payment-method"
              text={selectedPaymentMethod?.displayPaymentMethod ?? ''}
              className="csaa-qm-block"
            />
            <ReviewCardCommonEntry
              testId="payment-review-name-on-payment-method"
              label={
                selectedPaymentMethod?.analyticsAccountType ===
                'Debit/Credit Card'
                  ? 'Name on card'
                  : 'Name on account'
              }
              text={selectedPaymentMethod?.name ?? ''}
              className="csaa-qm-block"
            />
            {userEmail && (
              <ReviewCardCommonEntry
                label="Confirmation email"
                text={userEmail}
                testId="payment-review-confirmation-email"
                className="csaa-qm-encrypt"
              />
            )}
            {isSinglePayment && (
              <ReviewCardCommonEntry
                label={`${state.singleSelectedPayment.policyType} policy`}
                text={state.singleSelectedPayment.policyNumber}
                testId="payment-review-policy-number"
              />
            )}
            <ReviewCardCommonEntry
              label="Total"
              testId="payment-review-total"
              text={
                <NumericFormat
                  value={
                    isSinglePayment
                      ? state.singleSelectedPayment.amount
                      : getTotal()
                  }
                  displayType="text"
                  thousandSeparator
                  prefix="$"
                  fixedDecimalScale
                  decimalScale={2}
                />
              }
            />
          </Stack>
          {!isSinglePayment && isMobile && (
            <PaymentsCards policyNumbers={policyNumbers} />
          )}
          {!isSinglePayment && !isMobile && (
            <PaymentsTable
              rows={policyNumbers.map((policyNumber) => ({
                policyType: state.selectedPayments[policyNumber].policyType,
                policyNumber,
                policyStatus: state.selectedPayments[policyNumber].policyStatus,
                amount: state.selectedPayments[policyNumber].amount!,
              }))}
            />
          )}
          {showAutoPay && (
            <>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { mobile: 23, tablet: 28 },
                  fontWeight: 700,
                  lineHeight: { mobile: '32px', tablet: '40px' },
                  color: colors.csaaBlack,
                  letterSpacing: { mobile: 0.4, tablet: 0.6 },
                  mt: { mobile: 2, tablet: 3 },
                }}
              >
                Set up AutoPay
              </Typography>
              <Box sx={{ '&.MuiBox-root': { mt: 2.5 } }}>
                <FormControlLabel
                  componentsProps={{
                    typography: {
                      sx: {
                        fontFamily: 'Cabin',
                        fontSize: { mobile: 16, tablet: 18 },
                        lineHeight: '24px',
                        letterSpacing: { mobile: 0.3, tablet: 0.4 },
                      },
                    },
                  }}
                  control={
                    <Checkbox
                      checked={autoPayChecked}
                      onChange={() => setAutoPayChecked(!autoPayChecked)}
                      size="small"
                      name="setupAutoPayCheckbox"
                      data-testid="setupAutoPayCheckbox"
                      sx={{
                        py: 0,
                        '&.Mui-checked': {
                          color: colors.csaaBlack,
                        },
                        '&.Mui-focusVisible .MuiSvgIcon-root ': {
                          borderRadius: 1,
                          outline: `2px solid ${colors.csaaBlack}`,
                          outlineOffset: 1,
                        },
                      }}
                    />
                  }
                  label="Set up AutoPay"
                />
                <Typography
                  sx={{
                    fontFamily: 'Cabin',
                    fontSize: 14,
                    fontWeight: 400,
                    lineHeight: '16px',
                    color: colors.csaaLightBorder,
                    letterSpacing: 0.2,
                    mt: 1,
                    ml: 3,
                  }}
                >
                  Select “Set up AutoPay” and future payments for this policy
                  will be automatically withdrawn on the due date with the
                  selected payment method.
                </Typography>
              </Box>
            </>
          )}
          <Stack
            direction={{ mobile: 'column', tablet: 'row' }}
            justifyContent="flex-end"
            spacing={{ mobile: 1, tablet: 2 }}
            sx={{ mt: 3 }}
          >
            <Button
              component={Link}
              relative="path"
              to=".."
              variant="outlined"
              color="inherit"
              data-testid="paymentReviewSelectCancel"
              sx={{
                borderWidth: 2,
                fontWeight: 700,
                textTransform: 'none',
                fontSize: 18,
                color: 'text.secondary',
                px: 4,
                py: 0.5,
              }}
              onClick={(e: MouseEvent) => {
                e.preventDefault();
                navigate(-1);
              }}
            >
              Cancel
            </Button>
            <Button
              component={Link}
              to={isSinglePayment ? '/payment' : '/multiple-payment'}
              variant="outlined"
              color="inherit"
              data-testid="paymentReviewEditSelectCancel"
              sx={{
                borderWidth: 2,
                fontWeight: 700,
                textTransform: 'none',
                fontSize: 18,
                color: 'text.secondary',
                px: 4,
                py: 0.5,
              }}
            >
              Edit payment{isSinglePayment ? '' : 's'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              data-testid="paymentReviewPayButton"
              onClick={onPayClick}
              disabled={isSubmitting}
              sx={{
                fontWeight: 700,
                textTransform: 'none',
                fontSize: 18,
                px: 4,
                py: 0.5,
                '&:disabled': {
                  backgroundColor: 'primary.main',
                },
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={32} sx={{ color: 'white' }} />
              ) : (
                'Pay'
              )}
            </Button>
          </Stack>
          <Stack
            direction="column"
            justifyContent="flex-end"
            alignItems="flex-end"
            sx={{ mb: 7 }}
          >
            <Typography
              sx={{
                fontFamily: 'Cabin',
                fontKerning: 'normal',
                fontSize: 12,
                lineHeight: '16px',
                textAlign: { mobile: 'center', tablet: 'right' },
                letterSpacing: '0.2px',
                color: colors.csaaMid,
              }}
            >
              By submitting this payment, you agree to the{' '}
              <LinkWithNoRouter
                href="https://csaa-insurance.aaa.com/content/aaa-ie/b2c/en/misc/payment-terms.html"
                target="_blank"
                sx={{
                  fontFamily: 'inherit',
                  fontSize: 12,
                }}
                onClick={() =>
                  track('Terms and Conditions Accessed', {
                    event_type: 'Link Accessed',
                    link: 'Payment Terms and Conditions',
                    location: 'Feature Card',
                  })
                }
              >
                payment Terms and Conditions
              </LinkWithNoRouter>
            </Typography>
            {autoPayChecked && (
              <Typography
                variant="h1"
                sx={{
                  fontFamily: 'Cabin',
                  fontKerning: 'normal',
                  fontSize: 12,
                  lineHeight: '16px',
                  textAlign: { mobile: 'center', tablet: 'right' },
                  letterSpacing: '0.2px',
                  color: colors.csaaMid,
                }}
              >
                and the{' '}
                <LinkWithNoRouter
                  href="https://csaa-insurance.aaa.com/content/aaa-ie/b2c/en/misc/auth-terms.html"
                  target="_blank"
                  onClick={() =>
                    track('Terms and Conditions Accessed', {
                      event_type: 'Link Accessed',
                      link: 'paperless preferences terms and conditions',
                      location: 'Modal',
                    })
                  }
                  underline="none"
                  sx={{ fontSize: 12 }}
                >
                  AutoPay Terms and Conditions
                </LinkWithNoRouter>{' '}
              </Typography>
            )}
            {renderPaymentButton()}
          </Stack>
        </Card>
      </Stack>
    </>
  );
};

export default PaymentReviewPage;
